import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, DefinitionList, Dialog, Inline, Panel, Stack, t, Text, toast } from '@kanbios/ui';
import { RefusalPage } from '../../components/Refusal';
import { api, unwrap } from '../../lib/api';
import { clockTime, relativeTime } from '../../lib/format';
import { useSession } from '../../lib/session';

/**
 * Réglages › Accès aux serveurs (ADR-015): the platform's SSH certificate
 * authority, the addresses servers accept it from and its rotation. Bootstrap,
 * access check and enrolment are on the Servers list (several servers) and on
 * each server's page, Configuration tab (D30).
 */
export function AccessSection() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState<'rotate' | 'promote' | null>(null);
  const ca = useQuery({ queryKey: ['ssh-ca'], queryFn: async () => unwrap(await api.serverAccess.GET('/ca')) });
  const servers = useQuery({
    queryKey: ['server-access'],
    queryFn: async () => unwrap(await api.serverAccess.GET('/servers')),
    refetchInterval: query => (query.state.data?.checking ? 3_000 : 15_000),
  });
  const refresh = () => {
    for (const key of ['ssh-ca', 'ssh-bootstrap', 'server-access']) void queryClient.invalidateQueries({ queryKey: [key] });
  };
  const rotate = useMutation({
    mutationFn: async () => unwrap(await api.serverAccess.POST('/ca/rotation')),
    onSuccess: () => { toast({ title: t('settings.access.rotationPrepared'), tone: 'info' }); refresh(); },
  });
  const promote = useMutation({
    mutationFn: async () => unwrap(await api.serverAccess.POST('/ca/rotation/promote')),
    onSuccess: () => { toast({ title: t('settings.access.promoted'), tone: 'pos' }); refresh(); },
  });
  if (ca.error) return <RefusalPage error={ca.error} />;
  const active = ca.data?.items.find(c => c.status === 'active');
  const next = ca.data?.items.find(c => c.status === 'next');
  const items = servers.data?.items ?? [];
  const linux = items.filter(s => s.state !== 'not_applicable');
  const ready = linux.filter(s => s.state === 'trusted');
  const readyForNext = next ? linux.filter(s => s.trusts.includes(next.fingerprint)).length : 0;
  const manage = can('access.manage');

  return (
    <Stack gap={24}>
      <Panel
        title={t('settings.access.ca.title')}
        subtitle={t('settings.access.ca.subtitle')}
        // D30: bootstrap, access check and enrolment are on the Servers list and on each server's page.
        actions={manage && !next ? <Button variant="secondary" size="sm" icon="autorenew" onClick={() => setConfirm('rotate')}>{t('settings.access.rotate')}</Button> : undefined}
      >
        <Stack gap={16}>
          {ca.data?.keyMismatch && (
            <Alert tone="neg" title={t('settings.access.keyMismatch.title')}>{t('settings.access.keyMismatch.body')}</Alert>
          )}
          {ca.data?.egress?.allowed === false && (
            <Alert tone="neg" title={t('settings.access.egress.blockedTitle')}>
              {t('settings.access.egress.blockedBody', { ip: ca.data.egress.ip ?? '—', allowed: (ca.data.sourceAddresses ?? []).join(', ') })}
            </Alert>
          )}
          {next && (
            <Alert
              tone="info"
              title={t('settings.access.rotation.title')}
              action={manage ? <Button size="sm" onClick={() => setConfirm('promote')}>{t('settings.access.promote')}</Button> : undefined}
            >
              {t('settings.access.rotation.body', { fingerprint: next.fingerprint, n: readyForNext, total: linux.length })}
            </Alert>
          )}
          <Text variant="body-sm" tone="mute">{t('settings.access.perServer', { n: ready.length, total: linux.length })}</Text>
          <DefinitionList
            items={[
              { label: t('settings.access.ca.fingerprint'), value: active?.fingerprint ?? '—', mono: true },
              { label: t('settings.access.ca.principal'), value: ca.data?.principal ?? '—', mono: true },
              { label: t('settings.access.ca.sources'), value: ca.data?.sourceAddresses?.join(', ') || '—', mono: true },
              {
                label: t('settings.access.ca.egress'),
                value: ca.data?.egress?.ip
                  ? <Inline gap={8}><Text variant="code">{ca.data.egress.ip}</Text>{ca.data.egress.allowed !== null && <Badge tone={ca.data.egress.allowed ? 'pos' : 'neg'}>{t(ca.data.egress.allowed ? 'settings.access.egress.allowed' : 'settings.access.egress.refused')}</Badge>}</Inline>
                  : t('settings.access.egress.unknown'),
              },
              { label: t('settings.access.ca.created'), value: active ? `${clockTime(active.activatedAt ?? active.createdAt)} · ${relativeTime(active.activatedAt ?? active.createdAt)}` : '—' },
              { label: t('settings.access.ca.privateKey'), value: t('settings.access.ca.sealed') },
            ]}
          />
        </Stack>
      </Panel>



      <Dialog
        open={confirm === 'rotate'}
        onClose={() => setConfirm(null)}
        title={t('settings.access.rotateDialog.title')}
        description={t('settings.access.rotateDialog.body')}
        confirmLabel={t('settings.access.rotate')}
        onConfirm={() => { setConfirm(null); rotate.mutate(); }}
      />
      <Dialog
        open={confirm === 'promote'}
        onClose={() => setConfirm(null)}
        tone={readyForNext < linux.length ? 'danger' : 'default'}
        title={t('settings.access.promoteDialog.title')}
        description={t('settings.access.promoteDialog.body', { n: readyForNext, total: linux.length })}
        confirmLabel={t('settings.access.promote')}
        onConfirm={() => { setConfirm(null); promote.mutate(); }}
      />
    </Stack>
  );
}
